import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LcaPractitionerSelectInputRecord } from 'types/dynamic-forms/records/LcaPractitionerSelectInputRecord';
import usePropertyPath from '../../hooks/usePropertyPath';
import useProperty from '../../hooks/useProperty';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import useDataSource from '../../hooks/useDataSource';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { useDocumentStore } from '../../hooks/useDocumentStore';
import LcaProctitionerSelectInputRendererDialog from './LcaPractitionerSelectInputRendererDialog';
import { AddPractitionerToCompanyModel } from 'types/dynamic-forms/companies/AddPractitionerToCompanyModel';
import { postAddPractitionerToCompany } from 'services/api/dynamicFormsCompaniesApi';
import { toaster } from 'components/v2/toast';
import AuthService from 'services/AuthService';

interface LcaPractitionerData {
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  companyWebsite: string;
  companyEmail: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressCountry: string;
}

const LcaPractitionerSelectInputRenderer: React.FC<LcaPractitionerSelectInputRecord> = ({ propertyId }) => {
  const { t } = useTranslation();
  const store = useDocumentStore();
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, errorMessage, isDisabled, isVisible } = useProperty<LcaPractitionerData[]>(propertyPath);
  const userIdPath = useMemo(() => [...propertyPath, 0, 'userId'], [propertyPath]);
  const { options, invalidateDataSource } = useDataSource(userIdPath);
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);
  const isLcaPractitionerOnly = store.state.isLcaPractitionerOnly;

  // Hooks run before the visibility check: React needs the same hooks on every render.
  const selectedPractitioners = useMemo(() => {
    return options.filter((o) => (value ?? []).some((v) => v.userId === o.value));
  }, [options, value]);

  const companyPractitioners = useMemo(() => {
    return (value ?? []).map((v) => ({ value: v.userId, label: v.userName }));
  }, [value]);

  if (!isVisible) {
    return null;
  }

  const handleAddPractitioner = async (model: AddPractitionerToCompanyModel) => {
    const addResult = await postAddPractitionerToCompany(store.metadata.companyId, model);
    await invalidateDataSource();
    setValue([...value, addResult]);
  };

  const updateLcaPractitioners = async (userIds: string[] | null | undefined) => {
    // Clearing the whole selection (the clear-all control) arrives as an empty, null or undefined list.
    const nextUserIds = userIds ?? [];

    if (isLcaPractitionerOnly) {
      const currentUserId = AuthService.getUser()?.id;
      const isSelfRemoval =
        !!currentUserId && value.some((v) => v.userId === currentUserId) && !nextUserIds.includes(currentUserId);
      if (isSelfRemoval) {
        toaster({
          severity: 'error',
          summary: t('epdWizard.wizardStep1.properties.epdDeveloper.messages.restrictEPDDeveloperDeleteOwnEntry'),
        });
        return;
      }
    }

    const updatedPractitioners: LcaPractitionerData[] = nextUserIds.map((userId) => {
      const existingPractitioner = value.find((v) => v.userId === userId);
      if (existingPractitioner) {
        return existingPractitioner;
      }
      const newPractitioner = options.find((o) => o.value === userId);
      return newPractitioner?.meta as LcaPractitionerData;
    });
    setValue(updatedPractitioners);
  };

  return (
    <>
      <div>
        <WizardSelectControlledComponent
          name={propertyId}
          options={options}
          value={options.filter((o) => value.some((v) => v.userId === o.value))}
          label="LCA Practitioners"
          tooltip="The person accountable for the underlying Life Cycle Assessment."
          onChanged={(_: string, value: any) => updateLcaPractitioners(value)}
          error={errorMessage}
          disabled={isDisabled}
          isMulti
        />

        {!isDisabled && !isLcaPractitionerOnly && <WizardAddButton label="+ Add LCA Practitioner" onClick={() => setShowSelectDialog(true)} />}
      </div>

      <LcaProctitionerSelectInputRendererDialog
        isOpened={showSelectDialog}
        canPrefetch={!isDisabled}
        onHide={() => setShowSelectDialog(false)}
        companyId={store.metadata.companyId}
        selectedPractitioners={selectedPractitioners}
        companyPractitioners={companyPractitioners}
        onAddPractitioner={handleAddPractitioner}
      />
    </>
  );
};

export default LcaPractitionerSelectInputRenderer;
